import { supabase } from '../supabaseClient';
import { TestAttempt, TestSet } from '../types/models';
import { testSetService } from './testSetService';

export type TestAttemptWithDetails = {
  attempt: TestAttempt;
  testSet: TestSet;
  userAnswers: Record<string, Array<'A' | 'B' | 'C'>>;
}

export type CreateAttemptData = {
  testSetId: string;
  testSetName: string;
  score: number;
  totalQuestions: number;
  userAnswers: Record<string, Array<'A' | 'B' | 'C'>>;
};

export const attemptService = {
  getAttemptsForUser: async (userId: string): Promise<TestAttempt[]> => {
    const { data, error } = await supabase
      .from('test_attempts')
      .select('*, test_sets(name)')
      .eq('user_id', userId)
      .order('date_completed', { ascending: false });

    if (error) {
      console.error("Błąd pobierania historii prób:", error);
      return [];
    }

    // @ts-ignore
    return data.map(att => ({
      id: att.id,
      testSetId: att.test_set_id,
      testSetName: att.test_sets?.name || 'Nieznany test',
      score: att.score,
      totalQuestions: att.total_quesitions,
      dateCompleted: new Date(att.date_completed),
    }));
  },
  
  getAttemptWithDetailsById: async (attemptId: string): Promise<TestAttemptWithDetails | null> => {
    const { data: attemptData, error: attemptError } = await supabase
      .from('test_attempts')
      .select('*')
      .eq('id', attemptId)
      .single();

    if (attemptError || !attemptData) return null;

    const { data: answersData, error: answersError } = await supabase
      .from('attempt_answers')
      .select('question_id, selected_answers')
      .eq('attempt_id', attemptId);

    if (answersError) return null;

    const testSet = await testSetService.getTestSetById(attemptData.test_set_id);
    if (!testSet) return null;

    const userAnswers = (answersData || []).reduce((acc, answer) => {
      acc[answer.question_id] = answer.selected_answers;
      return acc;
    }, {} as Record<string, Array<'A' | 'B' | 'C'>>);
    
    return {
      attempt: {
        id: attemptData.id,
        testSetId: attemptData.test_set_id,
        testSetName: testSet.name,
        score: attemptData.score,
        totalQuestions: attemptData.total_quesitions,
        dateCompleted: new Date(attemptData.date_completed)
      },
      testSet,
      userAnswers
    };
  },

  getAllAttempts: async (): Promise<TestAttempt[]> => {
    const { data, error } = await supabase
      .from('test_attempts')
      .select('*, test_sets(name)');

    if (error) {
      console.error("Błąd pobierania wszystkich prób:", error);
      throw new Error(error.message);
    }
    
    // @ts-ignore
    return data.map(att => ({
      id: att.id,
      userId: att.user_id,
      testSetId: att.test_set_id,
      testSetName: att.test_sets?.name || 'Nieznany test',
      score: att.score,
      totalQuestions: att.total_quesitions,
      dateCompleted: new Date(att.date_completed),
    }));
  },

  saveAttempt: async (data: CreateAttemptData, userId: string): Promise<TestAttempt> => {
    const { data: attemptData, error: attemptError } = await supabase
      .from('test_attempts')
      .insert({
        user_id: userId,
        test_set_id: data.testSetId,
        score: data.score,
        total_quesitions: data.totalQuestions,
      })
      .select()
      .single();

    if (attemptError || !attemptData) {
      throw new Error(attemptError?.message || "Nie udało się zapisać próby - serwer nie zwrócił danych.");
    }
    
    const answersToInsert = Object.entries(data.userAnswers).map(([questionId, answers]) => ({
      attempt_id: attemptData.id,
      question_id: questionId,
      selected_answers: answers,
    }));

    if (answersToInsert.length > 0) {
        const { error: answersError } = await supabase
          .from('attempt_answers')
          .insert(answersToInsert);
        if (answersError) throw new Error(answersError.message);
    }
    
    return {
        id: attemptData.id,
        testSetId: attemptData.test_set_id,
        testSetName: data.testSetName,
        score: attemptData.score,
        totalQuestions: attemptData.total_quesitions,
        dateCompleted: new Date(attemptData.date_completed),
    };
  },
};